import React from 'react';
import { useParams } from 'react-router-dom';
import ArchitectureLayout from 'components/architecture/ArchitectureLayout';
import { getApiGroup, getGroupEndpoints } from 'utils/architecture/apiEndpoints';

/**
 * Concept explanations for each API group
 */
const CONCEPT_EXPLANATIONS = {
  tenants: {
    title: 'What is a Tenant?',
    natural: 'A tenant is an organization or company that uses the B-esper platform. Think of it as a separate workspace where all your resources, users, bots, and data are isolated from other organizations. Each tenant has its own configuration, billing, and access controls.',
    technical: 'Technically, a tenant is a multi-tenant isolation boundary in the B-esper platform. It represents a logical partition of the system where all resources (bots, knowledge bases, users, sessions, etc.) are scoped and isolated. Tenants enable multiple organizations to use the same platform infrastructure while maintaining complete data separation and independent configuration. Each tenant has a unique identifier and can have multiple users, resources, and associated metadata.'
  },
  users: {
    title: 'What is a User?',
    natural: 'A user is a person who has access to your tenant. Users can be team members, administrators, or anyone who needs to interact with your bots, manage resources, or access the customer portal. Each user has their own profile, preferences, and permissions.',
    technical: 'Technically, a user is an authenticated identity within a tenant context. Users are authenticated via Entra External ID (formerly Azure AD B2C) and are associated with a tenant. Each user has a unique identifier, profile information, role assignments, and preferences. Users can be assigned different security roles that determine their permissions and access levels within the tenant. User management includes CRUD operations, profile management, settings, and invitation workflows.'
  },
  resources: {
    title: 'What is a Resource?',
    natural: 'A resource is a bot or knowledge base that you create and manage in the B-esper platform. Resources are the core building blocks of your AI-powered solutions. Each resource can have its own configuration, knowledge sources, and behavior.',
    technical: 'Technically, a resource is a top-level entity in the B-esper platform that represents a deployable AI agent or knowledge base. Resources have a unique identifier, configuration settings, associated knowledge sources, and metadata. Resources can be bots (conversational AI agents) or knowledge bases (structured information repositories). Each resource belongs to a tenant and can be configured independently with its own settings, knowledge management, and operational parameters.'
  },
  knowledgeManagement: {
    title: 'What is Knowledge Management?',
    natural: 'Knowledge Management is how you teach your bots and knowledge bases. You can add information from websites, upload files, or create structured content. The system processes this information so your bots can answer questions and provide accurate responses based on your content.',
    technical: 'Knowledge Management is the system for ingesting, storing, indexing, and retrieving information for AI agents. It supports multiple knowledge sources including web scraping (websites), file uploads (PDFs, documents), and structured data. The system processes and indexes this content using vector embeddings and semantic search capabilities. Knowledge items can be enabled/disabled, have metadata, and are associated with specific resources. The system maintains scraping history, manages knowledge lifecycle, and provides search capabilities for retrieval-augmented generation (RAG) in conversational AI.'
  },
  sessions: {
    title: 'What is a Session?',
    natural: 'A session represents a conversation between a user and your bot. Each time someone starts chatting with your bot, a new session is created. Sessions track the conversation history, context, and state of the interaction.',
    technical: 'Technically, a session is a stateful interaction context between an end-user and an AI agent (bot). Sessions maintain conversation history, context, metadata, and state information. Each session has a unique identifier, is associated with a resource (bot), and can store messages, user information, timestamps, and other interaction data. Sessions enable continuity in conversations, context preservation, and analytics tracking. They can be created, retrieved, updated, and deleted through the API.'
  },
  analytics: {
    title: 'What is Analytics?',
    natural: 'Analytics provides insights into how your bots and resources are being used. You can see metrics like how many conversations happened, which questions were asked most often, user satisfaction, and performance statistics.',
    technical: 'Analytics is the system for collecting, aggregating, and reporting metrics and insights about platform usage. It tracks various dimensions including session counts, message volumes, resource utilization, user interactions, performance metrics, and business KPIs. Analytics data is aggregated per tenant, resource, or user and can be used for monitoring, optimization, and business intelligence. The system provides aggregated statistics, trends, and actionable insights through the analytics API.'
  },
  costPools: {
    title: 'What is a Cost Pool?',
    natural: 'A cost pool is a way to organize and track costs for different parts of your organization. You can group resources, users, or projects into cost pools to monitor spending and allocate costs for billing or budgeting purposes.',
    technical: 'A cost pool is a billing and cost allocation entity that groups resources, usage, or operations for financial tracking and billing purposes. Cost pools enable organizations to track expenses by department, project, resource type, or custom categorization. They aggregate usage metrics, compute costs, and provide cost attribution. Cost pools support multi-tenant billing scenarios, cost center management, and financial reporting. Each cost pool has a unique identifier, associated resources, billing rules, and cost tracking mechanisms.'
  },
  support: {
    title: 'What is Support?',
    natural: 'Support is the system for managing customer service tickets and help requests. You can create tickets when you need assistance, track their status, and communicate with the support team.',
    technical: 'Support is the ticketing system for customer service and technical assistance. Support tickets are structured records that track issues, requests, or inquiries. Each ticket has a unique identifier, status (open, in-progress, resolved, closed), priority, category, description, and associated metadata. Tickets can be created, updated, retrieved, and managed through the API. The system supports ticket lifecycle management, status tracking, assignment, and communication workflows.'
  },
  notifications: {
    title: 'What is a Notification?',
    natural: 'Notifications are messages that alert you about important events or updates in the platform. You might receive notifications about bot activity, system updates, billing information, or other important events.',
    technical: 'A notification is a message or alert that informs users or systems about events, updates, or important information. Notifications have a unique identifier, type, priority, content, target recipient, delivery status, and metadata. The notification system supports various notification types (system, user, resource, billing, etc.) and delivery channels. Notifications can be created, retrieved, updated, and deleted. They integrate with user preferences to control delivery and presentation.'
  },
  preferences: {
    title: 'What are Preferences?',
    natural: 'Preferences are your personal settings and choices for how the platform works for you. You can set notification preferences, language settings, display options, and other personalizations.',
    technical: 'Preferences are user-specific configuration settings that control behavior, appearance, and functionality of the platform for individual users. Preferences include notification settings (what notifications to receive, how, and when), display settings (language, theme, layout), operational preferences (default behaviors, shortcuts), and personalization options. Preferences are stored per user, can be retrieved and updated through the API, and affect how the system interacts with and presents information to the user.'
  },
  security: {
    title: 'What is Security?',
    natural: 'Security manages who can do what in your tenant. You can create roles (like "Administrator" or "Viewer"), assign permissions, and control access to different features and resources.',
    technical: 'Security is the access control and authorization system for the platform. It implements role-based access control (RBAC) where security roles define sets of permissions, and role assignments associate users with roles. Roles have unique identifiers, names, descriptions, and permission sets. Role assignments link users to roles within a tenant context. The security system controls access to resources, operations, and data based on these role assignments. It supports fine-grained permissions, hierarchical roles, and dynamic access control.'
  }
};

/**
 * Customer Portal API Group Page
 * Displays all endpoints within a specific API group
 */
const CustomerPortalApiGroup = () => {
  const { groupId } = useParams();
  const group = getApiGroup('customer', groupId);
  const endpoints = getGroupEndpoints('customer', groupId);

  if (!group) {
    return (
      <ArchitectureLayout
        title="Group Not Found"
        lastUpdated="2025-01-15"
        sections={[
          {
            id: 'error',
            title: 'Error',
            content: '<p>The requested API group could not be found.</p>'
          }
        ]}
      />
    );
  }

  const conceptExplanation = CONCEPT_EXPLANATIONS[groupId];

  // Generate code examples for the first few endpoints
  const generateCodeExamples = () => {
    const examples = [];
    const sampleEndpoints = endpoints.slice(0, 3); // Show examples for first 3 endpoints
    const baseUrl = window.location.origin;
    
    sampleEndpoints.forEach(ep => {
      const method = ep.methods[0]; // Use first method
      const pathParts = ep.path.replace('api/customer_portal/', '').split('/');
      const fullPath = `/v1/${pathParts.join('/')}`;
      const lastPart = pathParts[pathParts.length - 1];
      const requestBody = generateRequestBody(groupId, lastPart);
      
      let exampleTitle = `${method} ${ep.path}`;
      
      // Generate code for each language
      const codes = {
        curl: generateCurlCode(method, baseUrl, fullPath, requestBody),
        python: generatePythonCode(method, baseUrl, fullPath, requestBody),
        javascript: generateJavaScriptCode(method, baseUrl, fullPath, requestBody)
      };
      
      examples.push({
        title: exampleTitle,
        description: ep.description,
        codes: codes,
        code: codes.curl // Fallback for backwards compatibility
      });
    });
    
    return examples;
  };

  const generateCurlCode = (method, baseUrl, path, requestBody) => {
    if (method === 'GET') {
      return `curl -X GET "${baseUrl}${path}" \\
  -H "Authorization: Bearer YOUR_JWT_TOKEN" \\
  -H "Content-Type: application/json"`;
    } else if (method === 'POST') {
      return `curl -X POST "${baseUrl}${path}" \\
  -H "Authorization: Bearer YOUR_JWT_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '${JSON.stringify(requestBody, null, 2)}'`;
    } else if (method === 'PUT') {
      return `curl -X PUT "${baseUrl}${path}" \\
  -H "Authorization: Bearer YOUR_JWT_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '${JSON.stringify(requestBody, null, 2)}'`;
    } else if (method === 'DELETE') {
      return `curl -X DELETE "${baseUrl}${path}" \\
  -H "Authorization: Bearer YOUR_JWT_TOKEN"`;
    }
    return '';
  };

  const generatePythonCode = (method, baseUrl, path, requestBody) => {
    const hasBody = ['POST', 'PUT', 'PATCH'].includes(method);
    let code = `import requests\n\n`;
    code += `url = "${baseUrl}${path}"\n`;
    code += `headers = {\n`;
    code += `    "Authorization": "Bearer YOUR_JWT_TOKEN",\n`;
    code += `    "Content-Type": "application/json"\n`;
    code += `}\n\n`;
    
    if (hasBody) {
      code += `payload = ${JSON.stringify(requestBody, null, 2).split('\n').map((line, i) => i === 0 ? line : '    ' + line).join('\n')}\n\n`;
      code += `response = requests.${method.toLowerCase()}(url, headers=headers, json=payload)\n`;
    } else {
      code += `response = requests.${method.toLowerCase()}(url, headers=headers)\n`;
    }
    code += `print(response.json())`;
    
    return code;
  };

  const generateJavaScriptCode = (method, baseUrl, path, requestBody) => {
    const hasBody = ['POST', 'PUT', 'PATCH'].includes(method);
    let code = '';
    
    if (hasBody) {
      code += `const response = await fetch("${baseUrl}${path}", {\n`;
      code += `  method: "${method}",\n`;
      code += `  headers: {\n`;
      code += `    "Authorization": "Bearer YOUR_JWT_TOKEN",\n`;
      code += `    "Content-Type": "application/json"\n`;
      code += `  },\n`;
      code += `  body: JSON.stringify(${JSON.stringify(requestBody, null, 2).split('\n').map((line, i) => i === 0 ? line : '    ' + line).join('\n')})\n`;
      code += `});\n\n`;
    } else {
      code += `const response = await fetch("${baseUrl}${path}", {\n`;
      code += `  method: "${method}",\n`;
      code += `  headers: {\n`;
      code += `    "Authorization": "Bearer YOUR_JWT_TOKEN",\n`;
      code += `    "Content-Type": "application/json"\n`;
      code += `  }\n`;
      code += `});\n\n`;
    }
    
    code += `const data = await response.json();\n`;
    code += `console.log(data);`;
    
    return code;
  };

  const generateRequestBody = (groupId, endpointPart) => {
    // eslint-disable-next-line no-unused-vars
    const baseBody = {};
    
    switch (groupId) {
      case 'tenants':
        if (endpointPart.includes('independent')) {
          return { name: 'My Organization', email: 'contact@example.com' };
        } else if (endpointPart.includes('request-contact')) {
          return { message: 'I would like to get in touch', subject: 'Contact Request' };
        } else if (endpointPart.includes('apply-partner')) {
          return { company_name: 'My Company', contact_email: 'partner@example.com' };
        } else if (endpointPart.includes('apply-vendor')) {
          return { company_name: 'My Company', contact_email: 'vendor@example.com' };
        } else if (endpointPart.includes('apply-testimony')) {
          return { testimony: 'Great platform!', rating: 5 };
        }
        return { name: 'New Tenant', description: 'Tenant description' };
        
      case 'users':
        if (endpointPart.includes('invite')) {
          return { email: 'user@example.com', role: 'viewer' };
        } else if (endpointPart.includes('settings')) {
          return { theme: 'dark', language: 'en' };
        }
        return { email: 'user@example.com', name: 'John Doe' };
        
      case 'resources':
        if (endpointPart.includes('config')) {
          return { cors_enabled: true, api_key: 'your-api-key' };
        }
        return { name: 'My Bot', type: 'bot', description: 'A helpful assistant' };
        
      case 'knowledgeManagement':
        if (endpointPart.includes('add')) {
          return { source: 'https://example.com', type: 'website' };
        } else if (endpointPart.includes('websites')) {
          return { url: 'https://example.com', enabled: true };
        }
        return { content: 'Knowledge content', type: 'text' };
        
      case 'sessions':
        return { resource_id: 'resource-123', user_id: 'user-456' };
        
      case 'costPools':
        return { name: 'Marketing Department', description: 'Marketing costs' };
        
      case 'support':
        return { subject: 'Need help', message: 'I have a question', priority: 'medium' };
        
      case 'notifications':
        return { title: 'New notification', message: 'You have a new message', type: 'info' };
        
      case 'preferences':
        if (endpointPart.includes('notifications')) {
          return { email_enabled: true, push_enabled: false };
        }
        return { theme: 'dark', language: 'en' };
        
      case 'security':
        if (endpointPart.includes('roles')) {
          return { name: 'Administrator', permissions: ['read', 'write', 'delete'] };
        } else if (endpointPart.includes('role-assignments')) {
          return { user_id: 'user-123', role_id: 'role-456' };
        }
        return {};
        
      default:
        return {};
    }
  };

  const codeExamples = generateCodeExamples();

  const sections = [
    {
      id: 'overview',
      title: group.name,
      content: [
        `<p>${group.description}</p>`,
        `<p><strong>Base Path</strong>: <code>${group.basePath}</code></p>`
      ],
      codeExamples: codeExamples.length > 0 ? codeExamples : undefined
    },
    ...(conceptExplanation ? [{
      id: 'what-is',
      title: conceptExplanation.title,
      content: [
        '<h3>In Simple Terms</h3>',
        `<p>${conceptExplanation.natural}</p>`,
        '<h3>Technical Details</h3>',
        `<p>${conceptExplanation.technical}</p>`
      ]
    }] : []),
    {
      id: 'endpoints',
      title: 'Endpoints',
      content: [
        '<p>This group contains the following endpoints:</p>',
        '<ul>',
        ...endpoints.map(ep => 
          `<li><a href="/architecture/processes/customer-portal/${groupId}/${ep.endpointId}"><code>${ep.path}</code></a> - <strong>${ep.methods.join(', ')}</strong> - ${ep.description}</li>`
        ),
        '</ul>'
      ]
    },
    {
      id: 'navigation',
      title: 'Navigation',
      content: [
        '<p>Click on any endpoint above to view detailed documentation including:</p>',
        '<ul>',
        '<li>Technical description</li>',
        '<li>Request/response formats</li>',
        '<li>Authentication requirements</li>',
        '<li>Error handling</li>',
        '<li>Example requests</li>',
        '<li>Links to interactive API documentation</li>',
        '</ul>',
        `<p><a href="/architecture/processes/customer-portal">← Back to Customer Portal APIs</a></p>`
      ]
    }
  ];

  return (
    <ArchitectureLayout
      title={group.name}
      lastUpdated="2025-01-15"
      sections={sections}
    />
  );
};

export default CustomerPortalApiGroup;

